import { Alert } from "@package/subframe/components/Alert";
import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { FeatherCheck, FeatherX } from "@subframe/core";
import { format } from "date-fns";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  AddQuestionnaireConfigDtoAssigneeRolesItem,
  AddQuestionnaireConfigDtoCompletionMode,
  AddQuestionnaireConfigDtoFrequency,
  AddQuestionnaireConfigDtoMissBehaviour,
} from "@/generated/model";

import { AssigneeType, DAYS } from "./ScheduleTab";

interface PublishTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateName: string;
  questionCount: number;
  frequency: AddQuestionnaireConfigDtoFrequency;
  selectedDays: string[];
  dueByType: "end_of_day" | "custom";
  dueByTime: string;
  availableFromTime: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  assigneeType: AssigneeType;
  assigneeRoles: AddQuestionnaireConfigDtoAssigneeRolesItem[];
  assigneeDisplay: string;
  nurseryName: string | undefined;
  missBehaviour: AddQuestionnaireConfigDtoMissBehaviour;
  availableFromDayOfMonth: number;
  dueByDayOfMonth: number;
  availableFromDayOfWeek: string;
  dueByDayOfWeek: string;
  completionMode: AddQuestionnaireConfigDtoCompletionMode;
  isPublishing: boolean;
  onPublish: () => void;
}

export function PublishTemplateDialog({
  open,
  onOpenChange,
  templateName,
  questionCount,
  frequency,
  selectedDays: _selectedDays,
  dueByType,
  dueByTime,
  availableFromTime,
  startDate,
  endDate,
  assigneeType: _assigneeType,
  assigneeRoles: _assigneeRoles,
  assigneeDisplay,
  nurseryName,
  missBehaviour,
  availableFromDayOfMonth,
  dueByDayOfMonth,
  availableFromDayOfWeek,
  dueByDayOfWeek,
  completionMode,
  isPublishing,
  onPublish,
}: PublishTemplateDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 sm:max-w-[576px]" showCloseButton={false}>
        <div className="flex h-full w-full flex-col items-start bg-default-background">
          <div className="flex w-full flex-col items-start gap-4 px-8 py-6 overflow-auto">
            <div className="flex w-full items-center justify-between py-2">
              <span className="text-heading-2 font-heading-2 text-primary-navy-800">
                Publish this template?
              </span>
              <IconButton
                variant="neutral-tertiary"
                icon={<FeatherX />}
                onClick={() => onOpenChange(false)}
              />
            </div>

            <div className="flex w-full flex-col items-start gap-4">
              {/* Template details */}
              <div className="flex w-full flex-col items-start gap-2 rounded-sm bg-neutral-50 px-4 py-4">
                <span className="text-body-bold font-body-bold text-default-font">
                  Template details
                </span>
                <div className="flex w-full items-center justify-between">
                  <span className="text-caption font-caption text-subtext-color">
                    Template name
                  </span>
                  <span className="text-caption font-caption text-default-font">
                    {templateName}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-caption font-caption text-subtext-color">
                    Questions
                  </span>
                  <span className="text-caption font-caption text-default-font">
                    {questionCount} questions
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-caption font-caption text-subtext-color">
                    Status
                  </span>
                  <Badge variant="success" icon={<FeatherCheck />}>
                    Ready to publish
                  </Badge>
                </div>
              </div>

              {/* Schedule */}
              <div className="flex w-full flex-col items-start gap-2 rounded-sm bg-neutral-50 px-4 py-4">
                <span className="text-body-bold font-body-bold text-default-font">
                  Schedule
                </span>
                <div className="flex w-full items-center justify-between">
                  <span className="text-caption font-caption text-subtext-color">
                    Frequency
                  </span>
                  <span className="text-caption font-caption text-default-font">
                    {frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME
                      ? "One-time"
                      : frequency === AddQuestionnaireConfigDtoFrequency.DAILY
                        ? "Every day"
                        : frequency ===
                            AddQuestionnaireConfigDtoFrequency.MONTHLY
                          ? `Monthly, day ${availableFromDayOfMonth} to day ${dueByDayOfMonth}`
                          : `Every week, ${DAYS.find((d) => d.key === availableFromDayOfWeek)?.label} to ${DAYS.find((d) => d.key === dueByDayOfWeek)?.label}`}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-caption font-caption text-subtext-color">
                    If missed
                  </span>
                  <span className="text-caption font-caption text-default-font">
                    {missBehaviour ===
                    AddQuestionnaireConfigDtoMissBehaviour.HARD_MISS
                      ? "Hard miss"
                      : "Late completion allowed"}
                  </span>
                </div>
                {frequency === AddQuestionnaireConfigDtoFrequency.ONE_TIME ? (
                  <>
                    {startDate && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-caption font-caption text-subtext-color">
                          Start date
                        </span>
                        <span className="text-caption font-caption text-default-font">
                          {format(startDate, "EEEE, MMMM d, yyyy")} at{" "}
                          {availableFromTime}
                        </span>
                      </div>
                    )}
                    {endDate && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-caption font-caption text-subtext-color">
                          Due date
                        </span>
                        <span className="text-caption font-caption text-default-font">
                          {format(endDate, "EEEE, MMMM d, yyyy")} at {dueByTime}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex w-full items-center justify-between">
                      <span className="text-caption font-caption text-subtext-color">
                        Due by
                      </span>
                      <span className="text-caption font-caption text-default-font">
                        {dueByType === "end_of_day" ? "End of day" : dueByTime}
                      </span>
                    </div>
                    {startDate && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-caption font-caption text-subtext-color">
                          Runs from
                        </span>
                        <span className="text-caption font-caption text-default-font">
                          {format(startDate, "EEEE, MMMM d, yyyy")}
                        </span>
                      </div>
                    )}
                    {endDate && (
                      <div className="flex w-full items-center justify-between">
                        <span className="text-caption font-caption text-subtext-color">
                          Stops on
                        </span>
                        <span className="text-caption font-caption text-default-font">
                          {format(endDate, "EEEE, MMMM d, yyyy")}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Assignment */}
              <div className="flex w-full flex-col items-start gap-2 rounded-sm bg-neutral-50 px-4 py-4">
                <span className="text-body-bold font-body-bold text-default-font">
                  Assignment
                </span>
                <div className="flex w-full items-center justify-between">
                  <span className="text-caption font-caption text-subtext-color">
                    Assigned to
                  </span>
                  <span className="text-caption font-caption text-default-font">
                    {assigneeDisplay}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-caption font-caption text-subtext-color">
                    Nurseries
                  </span>
                  <span className="text-caption font-caption text-default-font">
                    {nurseryName || "All nurseries"}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-caption font-caption text-subtext-color">
                    Completion
                  </span>
                  <span className="text-caption font-caption text-default-font">
                    {completionMode ===
                    AddQuestionnaireConfigDtoCompletionMode.INDIVIDUAL
                      ? "Each member must complete"
                      : "Any member can complete"}
                  </span>
                </div>
              </div>
            </div>

            <Alert
              variant="brand"
              title="What happens when you publish?"
              description="This template will become active and practitioners will start receiving checklists according to the schedule. You can stop or edit the schedule at any time."
            />

            <div className="flex w-full items-center justify-end gap-2 pt-4">
              <Button
                variant="neutral-secondary"
                size="large"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                variant="brand-primary"
                size="large"
                onClick={onPublish}
                disabled={isPublishing}
              >
                {isPublishing ? "Publishing..." : "Publish template"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
